<div class="media-library layout-spacing">
    <!-- The to be projected content into the subnavbar -->
    <ng-template #subNavBarContent>
        <ntv-button size="lg" variant="accent" (buttonClick)="showUploader.set(true)">
            <div class="flex items-center gap-4">
                <span>Upload Media</span>
                <span>
                    <svg width="14" height="14" color="white">
                        <use xlink:href="assets/icons/icons-sprite.svg#upload"></use>
                    </svg>
                </span>
            </div>
        </ntv-button>
    </ng-template>

    <!-- Main Content -->
    @if (!showForTM) {
        <div
            class="media-library__stats"
            [style.grid-template-columns]="'repeat(' + numberOfStatsCard() + ', 1fr)'">
            @if (isLoadingStats() && stats().length === 0) {
                @for (i of [1, 2, 3, 4]; track i) {
                    <app-stats-card [isLoading]="true" />
                }
            } @else {
                @for (stat of stats(); track stat.title) {
                    <app-stats-card [statsData]="stat" />
                }
            }
        </div>
    }

    <div class="media-library__controls">
        <div class="controls__left">
            <app-dropdown
                [options]="mediaTypeOptions"
                placeholder="Media Type"
                (selectionChange)="onFilterByContentType($event)" />
            @if (!showForTM) {
                @for (btn of mediaLibraryButtons.leftControls; track btn.action) {
                    <ntv-button
                        size="xl"
                        [variant]="$any(btn.variant)"
                        (buttonClick)="stringToFnCall(btn.action)">
                        <div class="btn--expanded">
                            <span class="btn--expanded__text">{{
                                resolveButtonText(btn.label ?? '', btn.id)
                            }}</span>
                            <span>
                                <svg [attr.width]="btn.width" [attr.height]="btn.height">
                                    <use
                                        [attr.xlink:href]="
                                            'assets/icons/icons-sprite.svg#' + btn.icon
                                        "></use>
                                </svg>
                            </span>
                        </div>
                    </ntv-button>
                }
            }
        </div>
        <div class="controls__right">
            <app-searchbar
                placeholder="Search a content..."
                (searchTriggered)="onSearchContent($event)" />
            @if (!showForTM) {
                @for (btn of mediaLibraryButtons.rightControls; track btn.action) {
                    <ntv-button
                        size="xl"
                        [variant]="$any(resolveButtonVariant($any(btn.variant), $any(btn.label)))"
                        [disabled]="isBulkSelectDisabled(btn.disabled)"
                        (buttonClick)="stringToFnCall(btn.action)">
                        {{ btn.label }}
                    </ntv-button>
                }
                @for (btn of mediaLibraryButtons.viewControls; track btn.action) {
                    <svg
                        class="icon icon--hoverable"
                        [class.active]="currentViewType() === btn.action"
                        width="40"
                        height="40"
                        (click)="onSetViewType($any(btn.action))">
                        <use [attr.xlink:href]="'assets/icons/icons-sprite.svg#' + btn.icon"></use>
                    </svg>
                }
            }
        </div>
    </div>

    @if (currentViewType() === 'grid') {
        @if ((isLoadingContents() || isSearchingContents()) && !isLoadingMoreContents()) {
            <app-thumbnail-skeleton [skeletonCount]="40" skeletonSize="content-grid" />
        } @else if (contents().length > 0) {
            <div class="media-library__grid grid-contents">
                @for (item of contents(); track item.contentId) {
                    <div class="media-library__thumb">
                        <ntv-thumbnail-item
                            [disableRightClick]="true"
                            [menuOptionVisible]="false"
                            [selectable]="enableBulkSelect()"
                            [selectedIds]="selectedItemIds()"
                            [(selected)]="$any(item).selected"
                            [clickable]="true"
                            [item]="$any(item)"
                            size="lg"
                            [customWidth]="'100%'"
                            [customHeight]="'172px'"
                            [customTextSize]="'12px'"
                            [showActionButtons]="false"
                            [attr.data-content-id]="item.contentId"
                            (selectionToggle)="onSelectionToggle($event)"
                            (viewClick)="showMediaDetails($event)"
                            (editClick)="onContextMenuClick($event)"
                            (copyClick)="onCopyContentUrl($event)"
                            (downloadClick)="onDownloadContent($event)" />
                        <button
                            type="button"
                            class="media-library__thumb-delete"
                            title="Delete content"
                            data-testid="media-library-thumb-delete"
                            (click)="onDeleteRequested($any(item)); $event.stopPropagation()">
                            <svg width="11" height="11" viewBox="0 0 14 14">
                                <use href="assets/icons/icons-sprite.svg#delete"></use>
                            </svg>
                        </button>
                    </div>
                }
            </div>
        } @else {
            <app-empty title="No Records Found" animationWidth="300px" animationHeight="300px" />
        }
    } @else {
        <div class="media-library__contents-table">
            <app-table
                [columns]="tableColumns"
                [data]="contents()"
                [hasCheckBox]="true"
                [showColumnSettings]="false"
                [loading]="isLoadingContents()"
                [showItemCount]="false"
                tableHeight="auto"
                (actionClick)="onTableAction($event.action, $event.row)"
                (cellClick)="onCellClick($event)"
                (selectionChange)="onSelectionChange($event)">
                <!-- Template for the tag -->
                <ng-template ntvTableCell="tag" let-row="row">
                    <div [class]="getTagColor(row.type)">
                        {{ row.type.toUpperCase() }}
                    </div>
                </ng-template>

                <ng-template ntvTableCell="createdAt" let-row="row">
                    <div>
                        {{ row.createdAt | date: 'MMMM dd, yyyy' }}
                    </div>
                </ng-template>

                <ng-template ntvTableCell="dateCreated" let-row="row">
                    <p>{{ row.dateCreated }}</p>
                </ng-template>
            </app-table>
        </div>
    }

    <div class="media-library__pagination">
        @if (contents().length > 0) {
            <p class="media-library__pagination-text">
                @if (isLoadingContents() && !isLoadingMoreContents()) {
                    <p>Loading...</p>
                } @else {
                    Showing {{ contents().length }} out of
                    {{ paginationMeta().totalCount }}
                }
            </p>
        }
        @if (paginationMeta().hasNextPage) {
            <button class="media-library__pagination-btn" (click)="onNextPage()">
                @if (isLoadingMoreContents()) {
                    <p>Loading...</p>
                } @else {
                    <span>Show More</span>
                    <svg width="14" height="14">
                        <use
                            [attr.xlink:href]="'assets/icons/icons-sprite.svg#chevron-right'"></use>
                    </svg>
                }
            </button>
        }
    </div>
</div>

<!-- Media Details Modal -->
@if (showMediaDetailsModal() && selectedItem(); as item) {
    <app-media-details-modal
        [isVisible]="showMediaDetailsModal()"
        [item]="selectedItem()"
        [contents]="contents()"
        (modalClose)="showMediaDetailsModal.set(false)"
        (navigate)="selectedItem.set($event)"
        (deleteItem)="onDeleteContent()" />
}

<!-- Delete Confirmation Modal -->
@if (showDeleteConfirmationModal()) {
    <app-confirmation-modal
        type="warning"
        [isVisible]="showDeleteConfirmationModal()"
        (modalClose)="onCancelDelete()"
        (cancel)="onCancelDelete()"
        (confirm)="onDeleteContent()"
        [showCancelButton]="true"
        [isLoading]="isDeletingContents()"
        title="Confirm Deletion"
        [message]="
            selectedItem()?.name
                ? 'Are you sure you want to delete ' +
                  $safeNavigationMigration(selectedItem()?.name) +
                  '?'
                : 'Are you sure you want to delete the selected items?'
        "
        size="medium">
    </app-confirmation-modal>
}

<!-- Success Delete Modal -->
@if (showDeleteSuccessModal()) {
    <app-confirmation-modal
        type="success"
        [isVisible]="showDeleteSuccessModal()"
        [loopAnimation]="false"
        (modalClose)="onCancelDelete()"
        (confirm)="onDeleteSuccess()"
        title="Success"
        message="Successfully deleted item"
        size="small">
    </app-confirmation-modal>
}

<!-- Filter by user modal -->
@if (showFilterByUserModal()) {
    <app-filter-by-user-modal
        [(isVisible)]="showFilterByUserModal"
        [(selectedDealer)]="filterByUser().selectedDealer"
        [(selectedHost)]="filterByUser().selectedHost"
        [(selectedAdvertiser)]="filterByUser().selectedAdvertiser"
        (modalClose)="showFilterByUserModal.set(false)"
        (filter)="onFilterByUser($event)" />
}

<!-- Edit content modal -->
@if (showEditContentModal()) {
    <ntv-modal
        [isVisible]="showEditContentModal()"
        backdrop="blur"
        (modalClose)="showEditContentModal.set(false)">
        <div class="edit-modal">
            <div class="edit-modal__header">
                <div class="edit-modal__header-left">
                    <div class="edit-modal__icon-box">
                        <svg width="20" height="20">
                            <use xlink:href="assets/icons/icons-sprite.svg#pencil-edit"></use>
                        </svg>
                    </div>
                    <div class="edit-modal__title-group">
                        <h2>Edit Content Name</h2>
                        <p>Modify the display name of this media content</p>
                    </div>
                </div>
                <div class="edit-modal__close" (click)="showEditContentModal.set(false)">
                    <svg width="24" height="24">
                        <use xlink:href="assets/icons/icons-sprite.svg#close"></use>
                    </svg>
                </div>
            </div>

            @if (selectedItem()) {
                <div class="edit-modal__body mt-8 flex flex-col gap-6">
                    <div class="flex flex-col gap-2">
                        <label class="text-sm font-medium text-gray-700">Display Name</label>
                        <ntv-input
                            [ngModel]="editFileName()"
                            (ngModelChange)="editFileName.set($event)"
                            placeholder="Enter content name"></ntv-input>
                    </div>
                </div>

                <div class="edit-modal__footer mt-10 flex justify-end gap-3">
                    <ntv-button size="xl" (buttonClick)="showEditContentModal.set(false)">
                        Cancel
                    </ntv-button>
                    <ntv-button
                        variant="accent"
                        size="xl"
                        [loading]="isLoadingContents()"
                        (buttonClick)="onSaveEdit()"
                        [disabled]="!editFileName()">
                        Save Changes
                    </ntv-button>
                </div>
            }
        </div>
    </ntv-modal>
}

<!-- Dealer selection modal. Pre-upload content/media step -->
@if (showDealerSelectionModal()) {
    <ntv-modal
        [isVisible]="showDealerSelectionModal()"
        (modalClose)="showDealerSelectionModal.set(false)"
        size="large"
        [showFooter]="true">
        <div modal-header class="upload-modal__header">
            <div class="header__content">
                <h1 class="header__title">You are uploading content for:</h1>
                <p class="header__subtitle">Select a Dealer to get his/his Hosts and Advertiser</p>
            </div>
            <svg
                class="icon icon--hoverable close-icon"
                width="23"
                height="23"
                (click)="showDealerSelectionModal.set(false)">
                <use xlink:href="assets/icons/icons-sprite.svg#close"></use>
            </svg>
        </div>

        <app-dealer-selection-base
            [(selectedDealer)]="uploadContentFor().selectedDealer"
            [(selectedHost)]="uploadContentFor().selectedHost"
            [(selectedAdvertiser)]="uploadContentFor().selectedAdvertiser" />

        <div modal-footer class="upload-modal__footer">
            <div class="flex items-center gap-4">
                <ntv-button
                    variant="accent"
                    size="xl"
                    [disabled]="uploadContentFor().selectedDealer.length === 0"
                    (buttonClick)="onProceedToUpload()">
                    <span class="btn-span--md">Proceed</span>
                </ntv-button>
                <ntv-button size="xl" (buttonClick)="onSkipDealerSelection()"
                    ><span class="btn-span--md">Skip</span></ntv-button
                >
            </div>

            <div class="flex items-center gap-2">
                <ntv-toggle-button [(checked)]="uploadContentFor().optimizeVideoUpload" />
                <label class="text-sm font-medium">Optimize video upload (Recommended)</label>
            </div>
        </div>
    </ntv-modal>
}

<!-- Content/Media Uploader -->
@if (showUploader() && isBrowser) {
    <app-uploader
        [(showUploader)]="showUploader"
        [defaultOptimizeVideo]="uploadContentFor().optimizeVideoUpload"
        [dealerId]="uploadContentFor().selectedDealer[0]?.value ?? null"
        (onUploadSuccess)="onUploadFile($event)" />
}
